// Named ranges offered as one-click chips on BKG number and date filters.

export type RangePreset = {
  label: string;
  min?: number;
  max?: number;
};

export type DatePreset = {
  label: string;
  /** yyyy-mm-dd; the range runs from here with no end. */
  min: string;
};

// yyyy-mm-dd in local time; toISOString would move the day across UTC.
export const toDateInput = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const daysBefore = (today: Date, days: number) =>
  new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);

// Every BKG date filter looks back (report, event and funding dates), so the
// presets are open-ended "since" ranges.
export const getDatePresets = (today: Date): DatePreset[] => [
  { label: "Past 30 days", min: toDateInput(daysBefore(today, 30)) },
  { label: "Past 90 days", min: toDateInput(daysBefore(today, 90)) },
  {
    label: "Past 12 months",
    min: toDateInput(
      new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()),
    ),
  },
  { label: "This year", min: `${today.getFullYear()}-01-01` },
];

const MILLION = 1_000_000;
const BILLION = 1_000_000_000;

export const getRangePresets = (path: string | undefined): RangePreset[] => {
  if (!path) return [];
  const normalized = path.toLowerCase();

  const isCheckSizePath = normalized.includes("check_size_usd");
  const isAumPath = normalized.includes("aum_usd");
  const isFundSizePath =
    normalized.includes("size_usd") &&
    (normalized.includes("active_funds") ||
      normalized.includes("active_fund") ||
      normalized.includes("fund_size"));
  const isExitValuePath = normalized.includes("exit_value_usd");
  const isNumberOfEmployeesPath = normalized.includes("number_of_employees");
  const isLatestFundingPath =
    normalized.includes("latest_funding") && normalized.includes("amount");

  if (isCheckSizePath) {
    return [
      { label: "Micro Ticket (<$1M)", max: 1 * MILLION },
      {
        label: "Seed / Early Ticket ($1M–$5M)",
        min: 1 * MILLION,
        max: 5 * MILLION,
      },
      {
        label: "Series A / B Ticket ($5M–$20M)",
        min: 5 * MILLION,
        max: 20 * MILLION,
      },
      {
        label: "Growth Ticket ($20M–$100M)",
        min: 20 * MILLION,
        max: 100 * MILLION,
      },
      { label: "Mega Ticket (>$100M)", min: 100 * MILLION },
    ];
  }

  if (isAumPath || isFundSizePath) {
    return [
      {
        label: "Micro / Emerging ($10M–$50M)",
        min: 10 * MILLION,
        max: 50 * MILLION,
      },
      {
        label: "Early-stage ($50M–$200M)",
        min: 50 * MILLION,
        max: 200 * MILLION,
      },
      {
        label: "Multi-stage ($200M–$1B)",
        min: 200 * MILLION,
        max: 1 * BILLION,
      },
      { label: "Large / Growth ($1B–$5B)", min: 1 * BILLION, max: 5 * BILLION },
      { label: "Mega-fund (>$5B)", min: 5 * BILLION },
    ];
  }

  if (isExitValuePath) {
    return [
      { label: "Small Exit (<$50M)", max: 50 * MILLION },
      {
        label: "Mid-size Exit ($50M–$250M)",
        min: 50 * MILLION,
        max: 250 * MILLION,
      },
      { label: "Large Exit ($250M–$1B)", min: 250 * MILLION, max: 1 * BILLION },
      { label: "Mega Exit (>$1B)", min: 1 * BILLION },
    ];
  }

  if (isNumberOfEmployeesPath) {
    return [
      { label: "Startup (1–50)", min: 1, max: 50 },
      { label: "Small (51–250)", min: 51, max: 250 },
      { label: "Medium (251–1,000)", min: 251, max: 1000 },
      { label: "Large (1,001–3,000)", min: 1001, max: 3000 },
      { label: "Enterprise (>3,000)", min: 3001 },
    ];
  }

  if (isLatestFundingPath) {
    return [
      { label: "Seed (<$10M)", max: 10 * MILLION },
      { label: "Series A ($10M–$30M)", min: 10 * MILLION, max: 30 * MILLION },
      { label: "Series B ($30M–$100M)", min: 30 * MILLION, max: 100 * MILLION },
      {
        label: "Series C ($100M–$300M)",
        min: 100 * MILLION,
        max: 300 * MILLION,
      },
      { label: "Growth/Late (>$300M)", min: 300 * MILLION },
    ];
  }

  return [];
};
